import React from "react";
import {
  Box, VStack, HStack, Text, Center, IconButton, Button, Icon,
} from "@chakra-ui/react";
import { Image } from "@chakra-ui/react";
import { FiRotateCw, FiDownload, FiChevronLeft, FiChevronRight } from "react-icons/fi";

export function StoreImagesPanel({
  selectedStoreImages,
  currentImageIndex,
  setCurrentImageIndex,
  imageRotation,
  setImageRotation,
  onDownload,
}) {
  if (selectedStoreImages.length === 0) {
    return (
      <Center
        h="200px"
        bg="gray.50"
        borderRadius="xl"
        border="2px dashed"
        borderColor="gray.300"
      >
        <Text color="gray.500" fontWeight="medium">
          No store images available
        </Text>
      </Center>
    );
  }

  return (
    <VStack spacing={4}>
      {/* Main Image */}
      <Box
        position="relative"
        w="full"
        maxW="480px"
        mx="auto"
        borderRadius="xl"
        overflow="hidden"
        boxShadow="xl"
        bg="gray.100"
      >
        <Image
          src={selectedStoreImages[currentImageIndex]}
          alt={`Store image ${currentImageIndex + 1}`}
          objectFit="contain"
          w="100%"
          h="300px"
          bg="white"
          transform={`rotate(${imageRotation}deg)`}
          transition="transform 0.3s ease"
        />

        <IconButton
          aria-label="Rotate image"
          icon={<Icon as={FiRotateCw} />}
          position="absolute"
          top="2"
          right="10"
          colorScheme="blue"
          size="sm"
          onClick={() => setImageRotation((prev) => (prev + 90) % 360)}
          zIndex={2}
        />
        <IconButton
          aria-label="Download image"
          icon={<FiDownload />}
          position="absolute"
          top="2"
          right="2"
          colorScheme="red"
          size="sm"
          onClick={() => onDownload(selectedStoreImages[currentImageIndex])}
          zIndex={2}
        />

        {selectedStoreImages.length > 1 && (
          <>
            <Button
              position="absolute"
              left="4"
              top="50%"
              transform="translateY(-50%)"
              onClick={() => {
                setCurrentImageIndex(Math.max(0, currentImageIndex - 1));
                setImageRotation(0);
              }}
              isDisabled={currentImageIndex === 0}
              size="sm"
              bg="whiteAlpha.900"
              boxShadow="md"
              _hover={{ bg: "white" }}
              zIndex={1}
            >
              <Icon as={FiChevronLeft} boxSize={5} />
            </Button>
            <Button
              position="absolute"
              right="4"
              top="50%"
              transform="translateY(-50%)"
              onClick={() => {
                setCurrentImageIndex(
                  Math.min(selectedStoreImages.length - 1, currentImageIndex + 1)
                );
                setImageRotation(0);
              }}
              isDisabled={currentImageIndex === selectedStoreImages.length - 1}
              size="sm"
              bg="whiteAlpha.900"
              boxShadow="md"
              _hover={{ bg: "white" }}
              zIndex={1}
            >
              <Icon as={FiChevronRight} boxSize={5} />
            </Button>
          </>
        )}
      </Box>

      {/* Counter */}
      {selectedStoreImages.length > 1 && (
        <HStack>
          <Text fontSize="sm" color="gray.600" fontWeight="medium">
            {currentImageIndex + 1} / {selectedStoreImages.length}
          </Text>
        </HStack>
      )}

      {/* Thumbnails */}
      {selectedStoreImages.length > 1 && (
        <HStack spacing={2} overflowX="auto" py={2} justify="center">
          {selectedStoreImages.map((img, idx) => (
            <Box
              key={idx}
              onClick={() => setCurrentImageIndex(idx)}
              cursor="pointer"
              opacity={idx === currentImageIndex ? 1 : 0.5}
              border={idx === currentImageIndex ? "2px solid" : "none"}
              borderColor="red.500"
              borderRadius="md"
              overflow="hidden"
              transition="all 0.2s"
              _hover={{ opacity: 0.8 }}
            >
              <Image src={img} alt={`thumb ${idx}`} w="60px" h="60px" objectFit="cover" />
            </Box>
          ))}
        </HStack>
      )}
    </VStack>
  );
}
